/**
 * DOM helpers shared by the drawer ("show me where this is"), the tour (spotlight) and the
 * inspector (click a field, read what it does). Plain DOM, no React, no Fluent UI — split from
 * the components so all three agree on what "on screen" means. A guide that highlights a hidden
 * element is worse than one that says "not here right now".
 */
import type { HelpStep } from "./types";
import { anchorNameOf } from "./types";

/**
 * Marks the guide's own surfaces (the drawer, the tour card, the inspector's overlay) so they
 * are never mistaken for the dialog the user is working in — nor, in the inspector, for a
 * control the user was pointing at.
 */
export const HELP_PANEL_ATTR = "data-help-panel";

/** The attribute every guide step targets. Built as a constant so the checker sees no phantom. */
const ANCHOR_ATTR = "data-help";

/**
 * Z-order of the guide's own layers. Above the app's react-select menus (9999) and every Fluent
 * dialog (each portal container sits at 1000000, so dialogs stack by DOM order), below
 * react-toastify (9999999) so the guide's notices stay readable. The values below `portal` apply
 * inside a portal container, so they only beat a dialog when mounted after it. The drawer itself
 * has no z-index: it is a Fluent dialog and stacks like one — opened last, shown on top.
 */
export const HELP_Z = {
  /**
   * The launcher portals' mount node: one above Fluent's portal containers (1000000 each, dialogs
   * included), so the "?" outranks a dialog whichever was mounted first.
   */
  portal: 1_000_001,
  launcherCopy: 10001,
  overlay: 10020,
  card: 10021,
  banner: 10022,
  jump: 10025,
  tooltip: 10030,
} as const;

/**
 * Is this node actually showing? `getClientRects()` is empty for `display: none`, an unmounted
 * node and an element inside a collapsed container — cheaper and more honest than styles alone.
 */
const isVisible = (el: HTMLElement): boolean => {
  if (el.getClientRects().length === 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== "hidden" && style.display !== "none";
};

export const findStepElement = (
  selector: string,
  /** Restrict the lookup to one subtree — the open dialog, when there is one. */
  scope: ParentNode = document
): HTMLElement | null => {
  // Several copies of an anchor can exist (one per row, or a hidden duplicate for mobile):
  // the first VISIBLE one is the one the user can see.
  const candidates = Array.from(scope.querySelectorAll<HTMLElement>(selector));
  return candidates.find(isVisible) ?? null;
};

export const isStepOnScreen = (step: HelpStep, scope: ParentNode = document): boolean =>
  findStepElement(step.selector, scope) !== null;

/**
 * Anchor name → step, built once per guide so the inspector can resolve a pointer position
 * without re-scanning the step list on every mouse move. First wins — selectors are unique
 * within a guide (`registry.test.ts` proves it).
 */
export const anchorIndex = (steps: HelpStep[]): Map<string, HelpStep> => {
  const index = new Map<string, HelpStep>();
  for (const step of steps) {
    const anchor = anchorNameOf(step.selector);
    if (anchor && !index.has(anchor)) index.set(anchor, step);
  }
  return index;
};

export interface AnchoredStep {
  step: HelpStep;
  /** The element the step is actually about — the one to outline, not the one clicked. */
  element: HTMLElement;
}

/**
 * The guide entry for whatever the user is pointing at, walking outwards from the exact node
 * under the cursor (a click lands on an `<input>`, a label or an SVG path — never on the
 * element carrying `data-help`).
 *
 *  - **Innermost documented ancestor wins** — the more specific answer is the one asked for.
 *  - **An anchor with no step is walked THROUGH, not stopped at** — an undocumented attribute
 *    must not hide the documented section around it.
 *
 * The guide's own panels resolve to nothing.
 */
export const stepAtElement = (
  target: EventTarget | null,
  index: Map<string, HelpStep>
): AnchoredStep | null => {
  if (!(target instanceof Element)) return null;
  if (target.closest(`[${HELP_PANEL_ATTR}]`)) return null;

  let el = target.closest(`[${ANCHOR_ATTR}]`);
  while (el) {
    const step = index.get(el.getAttribute(ANCHOR_ATTR) ?? "");
    if (step && el instanceof HTMLElement) return { step, element: el };
    el = el.parentElement?.closest(`[${ANCHOR_ATTR}]`) ?? null;
  }
  return null;
};

/**
 * The modal dialog the user is currently working in, if any.
 *
 * A tour started while a dialog is open must stay INSIDE it: the page behind the backdrop is
 * not reachable. The topmost open dialog wins (portals append, so last in document order), and
 * the guide's own drawer and cards are excluded. Fluent v9 `DialogSurface` renders
 * `role="alertdialog"` for `modalType="alert"` — which most of this app's forms use — and
 * `role="dialog"` otherwise (`OverlayDrawer` included). A surface marked `data-help-ignore` (the
 * full-screen loading spinner) is never a work surface.
 */
export const activeDialogSurface = (): HTMLElement | null => {
  const open = Array.from(
    document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]')
  ).filter(
    (el) =>
      !el.hasAttribute(HELP_PANEL_ATTR) &&
      !el.hasAttribute("data-help-ignore") &&
      el.getClientRects().length > 0
  );
  return open.length > 0 ? open[open.length - 1] ?? null : null;
};

/** The app's brand stroke (purple), from the FluentProvider theme; hex as the fallback. */
const FLASH_COLOR = "var(--colorBrandStroke1, #5a2d82)";
const FLASH_MS = 1800;
/** The guide slides back once the outline has faded, plus a beat for the slide itself. */
const YIELD_MS = FLASH_MS + 250;

/**
 * Is the guide's own panel sitting on top of this control? The drawer takes the right edge,
 * which is exactly where the toolbars keep their primary actions — so it should move out of the
 * way, and only when it is genuinely covering the target.
 */
export const isOccludedByPanel = (el: HTMLElement): boolean => {
  const target = el.getBoundingClientRect();
  if (target.width === 0 && target.height === 0) return false;
  return Array.from(document.querySelectorAll<HTMLElement>(`[${HELP_PANEL_ATTR}]`)).some(
    (panel) => {
      const p = panel.getBoundingClientRect();
      if (p.width === 0 && p.height === 0) return false;
      return (
        target.left < p.right &&
        target.right > p.left &&
        target.top < p.bottom &&
        target.bottom > p.top
      );
    }
  );
};

/**
 * The panel is asked to step aside and to come back — never told how. `dom.ts` owns WHEN (it
 * owns the flash duration); the drawer owns HOW.
 */
export type YieldHandler = (yielding: boolean) => void;

let yieldTimer = 0;
let yieldingTo: YieldHandler | null = null;

export interface LocateOptions {
  /** Called with `true` when the guide is covering the target, and `false` once it may return. */
  onYield?: YieldHandler;
}

/**
 * Scroll a control into view and flash an outline around it. Returns false when the element is
 * not currently rendered, so the caller can explain why instead of doing nothing (the usual
 * cause: the step lives behind a dialog or a view that is not open).
 */
export const locateElement = (step: HelpStep, options: LocateOptions = {}): boolean => {
  const el = findStepElement(step.selector);
  if (!el) return false;

  if (options.onYield) {
    // While already yielded the panel is off-screen, so its rect no longer says anything about
    // what it covers: a locate during a yield extends it instead of re-measuring.
    if (yieldingTo !== null || isOccludedByPanel(el)) {
      window.clearTimeout(yieldTimer);
      yieldingTo = options.onYield;
      options.onYield(true);
      yieldTimer = window.setTimeout(() => {
        yieldingTo?.(false);
        yieldingTo = null;
      }, YIELD_MS);
    }
  }

  el.scrollIntoView({ behavior: "smooth", block: "center" });
  flash(el);
  return true;
};

/**
 * The element's own inline styles, saved before the first flash. A second "locate" within the
 * flash window must restore THESE, not the outline the first one put there — reading the styles
 * again would make the flash permanent.
 */
const flashing = new WeakMap<
  HTMLElement,
  { outline: string; offset: string; radius: string; timer: number }
>();

const flash = (el: HTMLElement): void => {
  const running = flashing.get(el);
  if (running) window.clearTimeout(running.timer);
  const saved = running ?? {
    outline: el.style.outline,
    offset: el.style.outlineOffset,
    radius: el.style.borderRadius,
    timer: 0,
  };
  el.style.outline = `3px solid ${FLASH_COLOR}`;
  el.style.outlineOffset = "3px";
  if (!saved.radius) el.style.borderRadius = "4px";
  saved.timer = window.setTimeout(() => {
    el.style.outline = saved.outline;
    el.style.outlineOffset = saved.offset;
    el.style.borderRadius = saved.radius;
    flashing.delete(el);
  }, FLASH_MS);
  flashing.set(el, saved);
};

/** Component libraries mark a disabled control either way, depending on the element it renders. */
const isPressable = (el: HTMLElement): boolean =>
  !el.hasAttribute("disabled") && el.getAttribute("aria-disabled") !== "true";

/** How long a pressed control gets to put a dialog on screen before we point at it instead. */
const OPENED_CHECK_MS = 700;
/** How much of a fresh budget the poll gets once a surface has been opened to render into. */
const SURFACE_GRACE_MS = 2500;

/**
 * Why the poll ended without the anchor being outlined. `behind-surface` is not quite a failure:
 * the control that LEADS to the anchor was flashed instead, because there was more than one of it
 * on screen and choosing between them is the user's call. `not-on-screen` is the honest one —
 * nothing was pointed at, and the caller owes the user a sentence saying so.
 */
export type MissReason = "not-on-screen" | "behind-surface";

export interface RevealAnchorOptions {
  /**
   * Press the control once it is found instead of only outlining it. Only for an Ask entry that
   * opted in (`anchor_action: open`), and only for controls that open a form and write nothing.
   */
  activate?: boolean;
  timeoutMs?: number;
  /** Same contract as `locateElement`: the drawer can be the very thing hiding the control. */
  onYield?: YieldHandler;
  /**
   * `anchor_surface` from the Ask entry: the control that opens the dialog the anchor lives
   * inside. Being on the right ROUTE is not being on the right SCREEN, and a form is not a route.
   */
  surface?: string | null;
  /**
   * Called once when the poll ends with the anchor never outlined. The one thing this function
   * must not do is finish quietly: "the button did nothing" is the bug this callback closes.
   */
  onMissing?: (reason: MissReason) => void;
  /**
   * Called just before a surface is opened. The guide's drawer is a dialog opened above the page,
   * so a form opened underneath it would be hidden — the drawer is told a form is coming.
   */
  onHandover?: () => void;
}

/**
 * Flash a `data-help` anchor by name — or press it — retrying until the screen that owns it has
 * rendered. The Ask tab navigates first and points second: after `navigate("/hr")` the page's data
 * is still loading, so a single lookup would find nothing.
 *
 * With `activate`, the same poll ends in a click, with two guards: a disabled control is flashed,
 * never pressed (greyed out is itself the answer to "why can't I do this?"), and a press that opens
 * no dialog falls back to flashing.
 *
 * With `surface`, the poll may open the dialog the anchor lives in first — but only when there is
 * exactly ONE such opener on screen: which record the user meant is never the panel's call.
 *
 * Whatever happens, the poll reports itself through `onMissing`. Returns a cancel function.
 */
export const flashAnchor = (anchor: string, options: RevealAnchorOptions = {}): (() => void) => {
  const {
    activate = false,
    timeoutMs = 4000,
    onYield,
    surface = null,
    onMissing,
    onHandover,
  } = options;
  const step: HelpStep = { selector: `[data-help="${anchor}"]`, title: anchor, body: "" };
  let deadline = Date.now() + timeoutMs;
  let timer = 0;
  let surfaceHandled = false;

  const press = (el: HTMLElement): void => {
    const before = activeDialogSurface();
    el.click();
    timer = window.setTimeout(() => {
      if (activeDialogSurface() === before) locateElement(step, { onYield });
    }, OPENED_CHECK_MS);
  };

  /** Returns whether the poll should continue. */
  const revealSurface = (name: string): boolean => {
    const visible = Array.from(
      document.querySelectorAll<HTMLElement>(`[data-help="${name}"]`)
    ).filter(isVisible);
    // Nothing yet — the route's data is still loading. Try again next tick.
    if (visible.length === 0) return true;

    surfaceHandled = true;
    const only = visible.length === 1 ? visible[0] : undefined;
    if (only && isPressable(only)) {
      onHandover?.();
      only.click();
      // A dialog mounts, then fetches, so it gets a floor of its own.
      deadline = Math.max(deadline, Date.now() + SURFACE_GRACE_MS);
      return true;
    }
    locateElement({ selector: `[data-help="${name}"]`, title: name, body: "" }, { onYield });
    onMissing?.("behind-surface");
    return false;
  };

  const attempt = (): void => {
    const el = findStepElement(step.selector);
    if (el) {
      if (activate && isPressable(el)) press(el);
      else locateElement(step, { onYield });
      return;
    }
    if (surface && !surfaceHandled && !revealSurface(surface)) return;
    if (Date.now() > deadline) {
      onMissing?.("not-on-screen");
      return;
    }
    timer = window.setTimeout(attempt, 160);
  };
  attempt();

  return () => window.clearTimeout(timer);
};
